import { Box, Chip, Typography } from '@mui/material';
import React from 'react';
import { STATUS_SALE } from '../../../../helpers/enums';
import { ccyFormat } from '../../../../helpers/helper';

// ─── single item row ──────────────────────────────────────────────────────────
const TicketRow = ({ sale, index, getPendingBySale }) => {
    const total = (sale.prices_sold?.sale_price || 0) * sale.quantity_sold;
    const hasDeposit = (sale.deposit || 0) > 0;
    const pending = hasDeposit && getPendingBySale ? getPendingBySale(sale) : 0;
    const isReserved = sale.status === STATUS_SALE.RESERVED;
    const articleName = sale.article?.name || 'Sin nombre';
    const isEven = index % 2 === 0;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                px: { xs: 1.5, sm: 2 },
                py: '5px',
                bgcolor: isEven ? 'transparent' : 'rgba(0,0,0,0.018)',
                borderBottom: '1px dashed',
                borderColor: 'rgba(0,0,0,0.06)',
                '&:last-of-type': { borderBottom: 'none' },
            }}
        >
            {/* main line */}
            <Box display='flex' alignItems='baseline' gap={0.5}>
                <Typography
                    sx={{
                        flex: 1,
                        fontSize: { xs: '0.8rem', sm: '0.86rem' },
                        fontWeight: 600,
                        color: '#222',
                        lineHeight: 1.3,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {articleName}
                </Typography>
                <Typography sx={{ fontSize: { xs: '0.72rem', sm: '0.76rem' }, color: '#888', fontWeight: 600, flexShrink: 0 }}>
                    {sale.quantity_sold}×
                </Typography>
                <Typography sx={{ fontSize: { xs: '0.83rem', sm: '0.88rem' }, fontWeight: 800, color: '#222', flexShrink: 0, minWidth: { xs: 48, sm: 56 }, textAlign: 'right' }}>
                    ${ccyFormat(total)}
                </Typography>
            </Box>

            {/* deposit sub-line */}
            {hasDeposit && isReserved && (
                <Box display='flex' gap={0.5} mt={0.3} flexWrap='wrap'>
                    <Chip
                        size='small'
                        label={`Seña $${ccyFormat(sale.deposit)}`}
                        sx={{
                            height: { xs: 21, sm: 22 },
                            bgcolor: '#FFF4CC',
                            color: '#9A6700',
                            border: '1px solid #F1D37A',
                            '& .MuiChip-label': {
                                px: { xs: 0.78, sm: 0.9 },
                                fontSize: { xs: '0.68rem', sm: '0.72rem' },
                                fontWeight: 800,
                            },
                        }}
                    />
                    {pending > 0 && (
                        <Chip
                            size='small'
                            label={`Debe $${ccyFormat(pending)}`}
                            sx={{
                                height: { xs: 21, sm: 22 },
                                bgcolor: '#FFC7CE',
                                color: '#9C0006',
                                border: '1px solid #E5A5AE',
                                '& .MuiChip-label': {
                                    px: { xs: 0.78, sm: 0.9 },
                                    fontSize: { xs: '0.68rem', sm: '0.72rem' },
                                    fontWeight: 800,
                                },
                            }}
                        />
                    )}
                </Box>
            )}
        </Box>
    );
};

// ─── date section with divider ────────────────────────────────────────────────
const TicketDateDivider = ({ date, groupTotal }) => {
    let formattedDate = date;
    try {
        const d = new Date(date);
        if (!Number.isNaN(d.getTime())) {
            formattedDate = d.toLocaleDateString('es-AR', { weekday: 'short', day: '2-digit', month: 'short' });
        }
    } catch (_) { /* keep original */ }

    return (
        <Box
            display='flex'
            alignItems='center'
            gap={0.75}
            sx={{ px: { xs: 1.5, sm: 2 }, py: '3px', borderBottom: '1px dashed rgba(0,0,0,0.08)' }}
        >
            <Typography sx={{ fontSize: { xs: '0.66rem', sm: '0.7rem' }, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>
                {formattedDate}
            </Typography>
            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(0,0,0,0.07)' }} />
            <Typography sx={{ fontSize: { xs: '0.66rem', sm: '0.7rem' }, fontWeight: 700, color: '#888', whiteSpace: 'nowrap' }}>
                ${ccyFormat(groupTotal)}
            </Typography>
        </Box>
    );
};

// ─── main export ──────────────────────────────────────────────────────────────
const CashierCollapseContent = ({ groupByDate, localSales, sales, getPendingBySale }) => {
    if (groupByDate && localSales && Object.keys(localSales).length > 0) {
        let globalIndex = 0;
        return (
            <Box>
                {Object.entries(localSales).map(([date, saleGroup]) => {
                    const groupTotal = saleGroup.reduce(
                        (acc, s) => acc + (s.prices_sold?.sale_price || 0) * s.quantity_sold, 0
                    );
                    return (
                        <Box key={date}>
                            <TicketDateDivider date={date} groupTotal={groupTotal} />
                            {saleGroup.map((sale) => (
                                <TicketRow
                                    key={sale._id || `${sale.article?.id}-${sale.created_at}`}
                                    sale={sale}
                                    index={globalIndex++}
                                    getPendingBySale={getPendingBySale}
                                />
                            ))}
                        </Box>
                    );
                })}
            </Box>
        );
    }

    return (
        <Box>
            {sales.sales.map((sale, i) => (
                <TicketRow
                    key={sale._id || sale.created_at}
                    sale={sale}
                    index={i}
                    getPendingBySale={getPendingBySale}
                />
            ))}
        </Box>
    );
};

export default CashierCollapseContent;
